import type { FC } from "react";

import type { GridConfig, PushBlockLogoProps, SecondButtonProps } from "@ui/components/PushBlock";
import { Typography } from "@ui/components/Typography";
import { Logo } from "@ui/eva-components/Logo";
import type { breakpoints } from "@ui/hooks/useCurrentBreakpoint";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import classNames from "classnames";
import DomPurify from "isomorphic-dompurify";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import type { ButtonSize } from "@jti/core-components/components/Button";
import { Button } from "@jti/core-components/components/Button";

import styles from "./PushBlockTextSection.module.scss";

const { sanitize } = DomPurify;

const breakpointToButtonSize: Record<keyof typeof breakpoints, ButtonSize> = {
  xs: "sm",
  sm: "sm",
  md: "md",
  lg: "lg",
};

export type PushBlockTextSectionType = {
  title: string;
  subtitle?: string;
  text?: string;
  cta?: string;
  isCentered: boolean;
  href?: string;
  smallOnMobile?: boolean;
  containedCTA?: boolean;
  logoProps?: PushBlockLogoProps;
  isFullHeightContent?: boolean;
  isFullWidthContent?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  secondButton?: SecondButtonProps;
  nativeBehaviour?: boolean;
  gridConfig?: GridConfig;
};

export const PushBlockTextSection: FC<PushBlockTextSectionType> = ({
  text,
  title,
  subtitle,
  isCentered,
  cta,
  href,
  smallOnMobile = false,
  containedCTA = false,
  onClick,
  logoProps,
  isFullHeightContent,
  isFullWidthContent,
  secondButton,
  nativeBehaviour = false,
  gridConfig,
}) => {
  const { mainBreakpoint, specificBreakpoint } = useCurrentBreakpoint();

  const isLarge = ["large", "largeMedium", "xLarge"].includes(specificBreakpoint);
  const isMedium = ["smallLarge", "medium", "mediumWide"].includes(specificBreakpoint);

  const tabletColumnWidth = gridConfig?.columnTabletWidth ?? gridConfig?.columnDesktopWidth;
  const shouldForceMobileLayout = gridConfig
    ? (isMedium && tabletColumnWidth !== "12") || (isLarge && gridConfig.columnDesktopWidth !== "12")
    : false;

  const buttonSize = shouldForceMobileLayout ? "sm" : breakpointToButtonSize[mainBreakpoint];
  const redirectButtonSize: ButtonSize = buttonSize === "lg" ? "md" : buttonSize;
  const isButtonEnabled = cta && href;

  return (
    <div
      className={classNames(styles.textSectionWrapper, {
        [styles.centered]: isCentered,
        [styles.fullHeightContent]: isFullHeightContent,
        [styles.fullWidthContent]: isFullWidthContent,
        [styles.smallOnMobile]: smallOnMobile,
        [styles.forceMobileLayout]: shouldForceMobileLayout,
      })}
    >
      <div className={styles.textSection}>
        {logoProps?.isLogoEnabled && (
          <div data-testid="logo">
            <Logo {...logoProps} className={styles.logo} />
          </div>
        )}
        <Typography variant="h2" className={styles.title}>
          {title}
        </Typography>
        {subtitle && (
          <Typography className={styles.subtitle} variant="h5" as="p">
            {subtitle}
          </Typography>
        )}
        {text && (
          <div
            data-testid="pushBlockText"
            className={styles.text}
            dangerouslySetInnerHTML={{ __html: sanitize(text) }}
          />
        )}
      </div>
      <div
        className={classNames(styles.buttonWrapper, {
          [styles.containedCTA]: containedCTA,
        })}
      >
        {isButtonEnabled && (
          <Button
            variant={containedCTA ? "primary" : "text-muted"}
            iconAfter={containedCTA ? undefined : LongArrowForwardIcon}
            size={containedCTA ? buttonSize : redirectButtonSize}
            onClick={onClick}
            href={href}
            nativeBehaviour={nativeBehaviour}
          >
            {cta}
          </Button>
        )}
        {secondButton?.isSecondButtonEnabled && (
          <Button variant="secondary" size={buttonSize} onClick={onClick} href={secondButton?.href}>
            {secondButton?.cta}
          </Button>
        )}
      </div>
    </div>
  );
};
